import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { useQuery } from '@common/hooks';
import {
  WORKFORCE_GROUP_FLAT_QUERY,
  WorkforceGroupResponse,
  SimplifiedWorkforceGroupType,
} from '@scheduling/graphql';
import { ErrorHandler, TableTransfer } from '@common/components';
import { TableColumnType } from 'antd';

type NormalizedData = {
  id: string;
  key: string;
  name: string;
};

const { filterOptionForColumns } = TableTransfer;

const tableColumns: TableColumnType<NormalizedData>[] = [
  {
    dataIndex: 'name',
    key: 'name',
    title: 'Workforce Group Name',
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
];

const normalizeData = (
  data: WorkforceGroupResponse | undefined
): NormalizedData[] =>
  (data?.workforceGroups || []).map((wfg) => ({
    id: wfg.id,
    key: wfg.id,
    name: wfg.name,
  }));

export function IncentivesUpdateSelectWorkforceGroupsStep({
  workforceGroups,
  onWorkforceGroupsChange,
}: {
  workforceGroups: SimplifiedWorkforceGroupType[];
  onWorkforceGroupsChange: Dispatch<
    SetStateAction<SimplifiedWorkforceGroupType[]>
  >;
}) {
  const [targetKeys, setTargetKeys] = useState(
    workforceGroups.map(({ id }) => id)
  );

  useEffect(() => {
    const newTargetKeys = workforceGroups.map(({ id }) => id);
    setTargetKeys(newTargetKeys);
  }, [workforceGroups]);

  const { loading, error, data } = useQuery<WorkforceGroupResponse>(
    WORKFORCE_GROUP_FLAT_QUERY
  );

  if (error) {
    return <ErrorHandler error={error} log="RatesOnDemandStepsStepTwo" />;
  }

  function onChange(newTargetKeys: string[]) {
    setTargetKeys(newTargetKeys);

    if (onWorkforceGroupsChange && data?.workforceGroups) {
      onWorkforceGroupsChange(
        data.workforceGroups
          .filter((wfg) => newTargetKeys.includes(wfg.id))
          .map((wfg) => ({ id: wfg.id, name: wfg.name }))
      );
    }
  }

  const filterOption = filterOptionForColumns(
    tableColumns.map(({ key }) => key)
  );

  const titles = {
    leftSingular: 'Workforce Group Available',
    rightSingular: 'Workforce Group Selected',
    leftPlural: 'Workforce Groups Available',
    rightPlural: 'Workforce Groups Selected',
  };

  return (
    <TableTransfer
      loading={loading}
      className="update-steps__step-one"
      dataSource={normalizeData(data)}
      targetKeys={targetKeys}
      disabled={loading}
      onChange={onChange}
      columns={tableColumns}
      showSearch
      filterOption={filterOption}
      titles={titles}
    />
  );
}
